import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { EdgeInstallerCatalog, EdgeInstallerDownload } from '../models';
import { AddMerchantLocation, ApiToken, AvailableLocation, CreateLocationUser, LocationUser, MerchantDetail, MerchantLocation, MerchantSummary, UpdateLocationUser, UpdateMerchantLocation, UpsertMerchant, DoorDashConnection, UpsertDoorDashConnection, DeliveryCostDetail,
    DeliveryCostReport, DeliveryStoreRow, DeliveryTracePage, DeliveryTraceSettings, EdgeDevice, EffectiveConfigView, RegisterDeliveryStore, UpdateDeliveryStoreDetails, CatalogCategoryTree, CatalogLocationStats, CatalogPage, CatalogProductDetail, CatalogProductListItem, CognitoGroup, CognitoUser, ConsumerControl, ConsumerLocation, CreateApiToken, DeleteLocationResult, FamilyDetail, FamilyListItem, FetchMagentoResult, GlobalMethodInfo, HealRun, IssuedTokenResponse, MagentoPayload, ProductTax, BrokerInfo, OutboxStatus, QueueCounts, LocationList, LocationMerchantInfo, LocationOrderDetail, LocationOrderPage, LocationTaxes, MagentoInstance, MethodConfigField, MethodEnvironmentInfo, OrderChangeVocabulary, OrderDeliveryDetail, OrderDetail, OrderLocationStats, OrderPage, OrderStatusHistory, OhlqCatalogPage, OhlqIngestResult, OhlqItemDetail, OhlqProductPage, OhlqSyncRun, OrderStreamEvent, OrderTimelinePage, PaymentDetail, PaymentFlow, PaymentTimelineEvent, PaymentMethodInfo, PaymentPage, PermissionNode, ProductChange, ProductFamilyAssemblyDetail, ProductFamilyPage, ProductOnlineOverrideList, ProductPriceHistory, ParkedMessage, ParkedMessages, ProductRequest, ProvisionResult, SetProductOnlineOverrideResult, OrderDispatches, OrderPrintState, OrderReceipt, ReplayErrorsResult, ReplayWebhooksResult, ReprintOrderResult, RolePermissions, SendToEdgeResult, SendToXtResult, SetXtStatusResult, SetEdgeStatusRequest, SetEdgeStatusResult, CancelOrderAtEdgeResult, StoreEdge, StoreEdgeUptime, StoreEdgeUsage, UpsertMethodEnvironment, UpsertPaymentMethod, UpsertXtConnection, XtConnection, XtExportSwitch, XtFulfilmentStatus, XtItemPage, XtItemRaw, XtPingResult, OrderHandoff, StalledHandoffs, StalledSendResponse } from '../models';

// Client for the MagentoAgent middleware API. The access token (when present) is attached
// automatically by apiAuthInterceptor (core/auth) — fed by our own /central/auth login.
@Injectable({ providedIn: 'root' })
export class MiddlewareApi {
    private readonly http = inject(HttpClient);
    private readonly base = environment.apiBaseUrl;

    // Central admin: instances list (no secrets) + enable/disable (enabling disables the others).
    getInstances(): Observable<MagentoInstance[]> {
        return this.http.get<MagentoInstance[]>(`${this.base}/central/admin/instances`);
    }

    setInstanceEnabled(id: string, enabled: boolean): Observable<MagentoInstance> {
        return this.http.put<MagentoInstance>(`${this.base}/central/admin/instances/${encodeURIComponent(id)}/enabled`, { enabled });
    }

    // Central admin: MassTransit consumer control panel. Desired state; pausing keeps the durable
    // queue bound (zero loss). The API links Order Snapshot / Order Lifecycle to prevent data loss.
    getConsumers(): Observable<ConsumerControl[]> {
        return this.http.get<ConsumerControl[]>(`${this.base}/central/admin/consumers`);
    }

    setConsumerEnabled(key: string, enabled: boolean): Observable<ConsumerControl> {
        return this.http.put<ConsumerControl>(`${this.base}/central/admin/consumers/${encodeURIComponent(key)}/enabled`, { enabled });
    }

    // Set or clear a CONSUMER's concurrency caps. Pass the base key (ConsumerControl.baseKey): the API
    // normalises a per-location key to its consumer anyway, and the setting applies to every one of that
    // consumer's location endpoints. The endpoints are rebound by the reconcile loop within a few
    // seconds, on every instance.
    //
    // The BASELINES are what the operator was shown when the editor opened. The editor prefills the
    // effective values, so the API needs them to tell "left alone" from "deliberately set to this
    // number" — a field still equal to its baseline keeps whatever was stored instead of pinning today's
    // default. Omit them for absolute semantics (the "use defaults" clear).
    /** Moves messages from a queue's _error sibling back onto the queue. Bounded per call. */
    /** Rebuild a merchant's broker side: create its vhost and bind its consumer queues. Idempotent, so it
     *  is safe as a "try again" — the failure it answers is a merchant that saved while the broker was
     *  unreachable and is therefore correct in the database and absent from RabbitMQ. */
    provisionLocation(locationId: string): Observable<ProvisionResult> {
        return this.http.post<ProvisionResult>(
            `${this.base}/central/admin/locations/${encodeURIComponent(locationId)}/provision`, {});
    }

    /** Retire a merchant: removes the record, unbinds its consumer queues and deletes its broker vhost,
     *  giving that slot back against the broker's vhost limit — a merchant deleted without its vhost would
     *  hold the slot forever. REFUSES with 409 while its queues still hold messages, because deleting a
     *  vhost destroys them; `force` is the operator agreeing to that, and is never what the first press
     *  sends. */
    deleteLocation(locationId: string, force = false): Observable<DeleteLocationResult> {
        const params = new HttpParams().set('force', String(force));
        return this.http.delete<DeleteLocationResult>(
            `${this.base}/central/admin/locations/${encodeURIComponent(locationId)}`, { params });
    }

    /** Read a queue's parked errors without consuming them, so an operator can tell an outage worth
     *  replaying from messages that will fail again. */
    peekConsumerErrors(consumerKey: string, locationId: string | null, max = 20): Observable<ParkedMessages> {
        let params: Record<string, string> = { max: String(max) };
        if (locationId) params = { ...params, locationId };
        return this.http.get<ParkedMessages>(
            `${this.base}/central/admin/consumers/${encodeURIComponent(consumerKey)}/errors`, { params });
    }

    replayConsumerErrors(consumerKey: string, locationId: string | null, max = 500): Observable<ReplayErrorsResult> {
        let params = new HttpParams().set('max', String(max));
        if (locationId) params = params.set('locationId', locationId);
        return this.http.post<ReplayErrorsResult>(
            `${this.base}/central/admin/consumers/${encodeURIComponent(consumerKey)}/replay-errors`, null, { params });
    }

    // The TARGET, and whether it is one pool for the consumer or a number per queue. `prefetchCount` is still
    // on the wire and the editor no longer sends anything but null for it: prefetch is derived, shown and not
    // editable. The one caller that sends a value is "Use defaults", which must be able to clear a legacy
    // prefetch override the form can no longer display.
    setConsumerConcurrency(
        key: string,
        prefetchCount: number | null,
        concurrentMessageLimit: number | null,
        prefetchBaseline: number | null = null,
        concurrencyBaseline: number | null = null,
        shared: boolean | null = null
    ): Observable<ConsumerControl> {
        return this.http.put<ConsumerControl>(
            `${this.base}/central/admin/consumers/${encodeURIComponent(key)}/concurrency`,
            { prefetchCount, concurrentMessageLimit, prefetchBaseline, concurrencyBaseline, shared });
    }

    // Every merchant's endpoint for a location-scoped consumer, connected or not. Built server-side from
    // the merchant table rather than from live endpoints, so it answers even when nothing is connected.
    getConsumerLocations(key: string): Observable<ConsumerLocation[]> {
        return this.http.get<ConsumerLocation[]>(
            `${this.base}/central/admin/consumers/${encodeURIComponent(key)}/locations`);
    }

    // Counts only, and cheap by design: the Consumers page polls this about once a second. The API serves
    // it from a ~1s cache of a single cross-vhost broker call, so many watchers do not become many
    // management calls.
    getConsumerQueueCounts(): Observable<QueueCounts> {
        return this.http.get<QueueCounts>(`${this.base}/central/admin/consumers/counts`);
    }

    // Orders the middleware has imported (read from our DB, where the consumer stored the full
    // Magento REST order at intake). Newest first, paged.
    getOrders(page = 1, pageSize = 50, source?: string): Observable<OrderPage> {
        let url = `${this.base}/central/orders?page=${page}&pageSize=${pageSize}`;
        if (source) url += `&source=${encodeURIComponent(source)}`;
        return this.http.get<OrderPage>(url);
    }

    getOrder(id: string): Observable<OrderDetail> {
        return this.http.get<OrderDetail>(`${this.base}/central/orders/${encodeURIComponent(id)}`);
    }

    // Re-pull this order from Magento (by its stored entity id) and re-parse it server-side,
    // overwriting the stored canonical payload. Backs the "Refetch from Magento" action.
    refetchOrder(id: string): Observable<unknown> {
        return this.http.post(`${this.base}/central/orders/${encodeURIComponent(id)}/refetch`, {});
    }

    // Replay this order's delivery webhooks (idempotent). Returns a per-webhook outcome the UI animates.
    replayWebhooks(id: string): Observable<ReplayWebhooksResult> {
        return this.http.post<ReplayWebhooksResult>(`${this.base}/central/orders/${encodeURIComponent(id)}/replay-webhooks`, {});
    }

    getProductRequests(): Observable<ProductRequest[]> {
        return this.http.get<ProductRequest[]>(`${this.base}/magento-agent/product-requests`);
    }

    // Locations onboarded in the currently active Magento instance (with merchant details).
    getLocations(): Observable<LocationList> {
        return this.http.get<LocationList>(`${this.base}/central/admin/locations`);
    }

    // Live mirror of a location's resolved taxes (same parser the tax sync uses).
    getLocationTaxes(locationId: string): Observable<LocationTaxes> {
        return this.http.get<LocationTaxes>(`${this.base}/central/admin/locations/${encodeURIComponent(locationId)}/taxes`);
    }

    // On-demand read-through of one merchant's details from Magento (nothing persisted). Unlike the
    // list's best-effort enrichment, a failure answers { success:false, error } carrying Magento's
    // actual reason — for the operator to read, not a log to hunt for.
    fetchLocationFromMagento(locationId: string): Observable<FetchMagentoResult> {
        return this.http.post<FetchMagentoResult>(`${this.base}/central/admin/locations/${encodeURIComponent(locationId)}/fetch-magento`, {});
    }

    // --- Merchants: the business that owns locations (read merchants:read; write merchants:manage) ---
    getMerchants(): Observable<MerchantSummary[]> {
        return this.http.get<MerchantSummary[]>(`${this.base}/central/admin/merchants`);
    }

    /** One merchant with its locations. 404 when unknown. Users come from getLocationUsers — merchants:read
     *  does not read their identities. */
    getMerchant(merchantId: string): Observable<MerchantDetail> {
        return this.http.get<MerchantDetail>(`${this.base}/central/admin/merchants/${encodeURIComponent(merchantId)}`);
    }

    createMerchant(body: UpsertMerchant): Observable<MerchantSummary> {
        return this.http.post<MerchantSummary>(`${this.base}/central/admin/merchants`, body);
    }

    updateMerchant(merchantId: string, body: UpsertMerchant): Observable<MerchantSummary> {
        return this.http.put<MerchantSummary>(`${this.base}/central/admin/merchants/${encodeURIComponent(merchantId)}`, body);
    }

    /** Refused with 409 while the merchant still has locations. */
    deleteMerchant(merchantId: string): Observable<void> {
        return this.http.delete<void>(`${this.base}/central/admin/merchants/${encodeURIComponent(merchantId)}`);
    }

    /** Onboarded location ids that are not under any merchant yet. */
    getAvailableLocations(): Observable<AvailableLocation[]> {
        return this.http.get<AvailableLocation[]>(`${this.base}/central/admin/merchants/available-locations`);
    }

    /** 409 when the id already belongs to a merchant; 400 when it is not onboarded and no name is given. */
    addMerchantLocation(merchantId: string, body: AddMerchantLocation): Observable<MerchantLocation> {
        return this.http.post<MerchantLocation>(`${this.base}/central/admin/merchants/${encodeURIComponent(merchantId)}/locations`, body);
    }

    updateMerchantLocation(merchantId: string, locationId: string, body: UpdateMerchantLocation): Observable<MerchantLocation> {
        return this.http.put<MerchantLocation>(
            `${this.base}/central/admin/merchants/${encodeURIComponent(merchantId)}/locations/${encodeURIComponent(locationId)}`, body);
    }

    /** Takes the location off the merchant and drops users' grants for it. Magento onboarding, orders,
     *  products and the broker are untouched — that is deleteLocation. */
    removeMerchantLocation(merchantId: string, locationId: string): Observable<void> {
        return this.http.delete<void>(
            `${this.base}/central/admin/merchants/${encodeURIComponent(merchantId)}/locations/${encodeURIComponent(locationId)}`);
    }

    // --- Location users: email + password sign-ins for the people at a location, NOT Cognito. Every route
    //     here needs location-users:manage, reads included ---
    /** merchantId narrows to users with any grant on that merchant. */
    getLocationUsers(merchantId?: string | null): Observable<LocationUser[]> {
        const params = merchantId ? new HttpParams().set('merchantId', merchantId) : new HttpParams();
        return this.http.get<LocationUser[]>(`${this.base}/central/admin/location-users`, { params });
    }

    createLocationUser(body: CreateLocationUser): Observable<LocationUser> {
        return this.http.post<LocationUser>(`${this.base}/central/admin/location-users`, body);
    }

    updateLocationUser(userId: string, body: UpdateLocationUser): Observable<LocationUser> {
        return this.http.put<LocationUser>(`${this.base}/central/admin/location-users/${encodeURIComponent(userId)}`, body);
    }

    /** Also clears a sign-in lockout. */
    setLocationUserPassword(userId: string, password: string): Observable<void> {
        return this.http.post<void>(`${this.base}/central/admin/location-users/${encodeURIComponent(userId)}/password`, { password });
    }

    deleteLocationUser(userId: string): Observable<void> {
        return this.http.delete<void>(`${this.base}/central/admin/location-users/${encodeURIComponent(userId)}`);
    }

    // --- Edge devices: machines that enrolled themselves and are waiting on a human (read merchants:read;
    //     deciding edges:manage) ---
    /** Already ordered by the API — undecided first, oldest first inside that, because a machine that has been
     *  waiting longest is the one somebody is standing next to. `status` narrows to one decision. */
    getEdgeDevices(status?: 'pending' | 'approved' | 'rejected' | null): Observable<EdgeDevice[]> {
        let params = new HttpParams();
        if (status) params = params.set('status', status);
        return this.http.get<EdgeDevice[]>(`${this.base}/central/admin/edge-devices`, { params });
    }

    /** Lets a machine in. No key travels through this call: the device collects one on its next contact, because
     *  a key's plaintext is never stored and one minted here would have nowhere to wait. Approving a device whose
     *  claimed location is not onboarded succeeds — it gets a key and heartbeats, and stays unbound until the
     *  store exists and is linked.
     *
     *  `replaceEdgeId` is required only when the device carries a `collision`: something is already registered
     *  as that (store, machine name) and approving re-keys it, so the API refuses with 409 and the collision in
     *  the body until the operator names that same Edge. */
    approveEdgeDevice(requestId: string, note?: string | null, replaceEdgeId?: string | null): Observable<EdgeDevice> {
        return this.http.post<EdgeDevice>(
            `${this.base}/central/admin/edge-devices/${encodeURIComponent(requestId)}/approve`,
            { note: note ?? null, replaceEdgeId: replaceEdgeId ?? null });
    }

    /** Turns one away. Final: the machine is told to stop asking and cannot return to the queue by calling again. */
    rejectEdgeDevice(requestId: string, note?: string | null): Observable<EdgeDevice> {
        return this.http.post<EdgeDevice>(
            `${this.base}/central/admin/edge-devices/${encodeURIComponent(requestId)}/reject`, { note: note ?? null });
    }

    /** Removes the request so that machine can enrol again — the way back for a PC that lost its enrolment secret
     *  (reimaged, restored without it), which is otherwise refused for ever. Any Edge it was bound to stays. */
    forgetEdgeDevice(requestId: string): Observable<void> {
        return this.http.delete<void>(`${this.base}/central/admin/edge-devices/${encodeURIComponent(requestId)}`);
    }

    // --- Store Edges: the machines installed at the stores, and which of them are up (read merchants:read;
    //     retire merchants:manage) ---
    /** Already ordered by the API — offline first, then the longest silent. Either filter narrows the list;
     *  the location one is what a single store's page asks for. */
    getStoreEdges(merchantId?: string | null, locationId?: string | null): Observable<StoreEdge[]> {
        let params = new HttpParams();
        if (merchantId) params = params.set('merchantId', merchantId);
        if (locationId) params = params.set('locationId', locationId);
        return this.http.get<StoreEdge[]>(`${this.base}/central/admin/edges`, { params });
    }

    /** The stretches one Edge was up over a window, newest window defaulting to the last day. The API caps a
     *  request at 90 days. Only up-time comes back: an outage is a gap between two stretches, so the caller
     *  derives the bad news from the good, and no job has to notice a store going quiet for the outage to
     *  exist. */
    getStoreEdgeUptime(edgeId: string, from?: string, to?: string): Observable<StoreEdgeUptime> {
        let params = new HttpParams();
        if (from) params = params.set('from', from);
        if (to) params = params.set('to', to);
        return this.http.get<StoreEdgeUptime>(
            `${this.base}/central/admin/edges/${encodeURIComponent(edgeId)}/uptime`, { params });
    }

    /** CPU, memory and disk for one Edge over a window, in five-minute buckets of average and peak. Kept for
     *  seven days; asking further back is not an error, the answer simply starts where the history does. */
    getStoreEdgeUsage(edgeId: string, from?: string, to?: string): Observable<StoreEdgeUsage> {
        let params = new HttpParams();
        if (from) params = params.set('from', from);
        if (to) params = params.set('to', to);
        return this.http.get<StoreEdgeUsage>(
            `${this.base}/central/admin/edges/${encodeURIComponent(edgeId)}/usage`, { params });
    }

    /** Retires an installation: the key stops working at once, so that machine must be set up again before
     *  it can serve the store. The location's orders, queues and onboarding are untouched — this is the
     *  record of a machine, not of the store. */
    deleteStoreEdge(edgeId: string): Observable<void> {
        return this.http.delete<void>(`${this.base}/central/admin/edges/${encodeURIComponent(edgeId)}`);
    }

    // --- The stop on every store's Exatouch export (read merchants:read; change edges:manage) ---
    /** Whether the stores are currently allowed to write orders into Exatouch. Absent history reads as
     *  running: nobody has ever pulled it. */
    getXtExportSwitch(): Observable<XtExportSwitch> {
        return this.http.get<XtExportSwitch>(`${this.base}/central/admin/edges/xt-export`);
    }

    /** Stops, or allows again, every store's export. Reaches each Edge on its next heartbeat - seconds - and
     *  a store that is off when it is pulled hears it when it comes back. Orders taken while stopped are
     *  queued at the store, not lost, and are written the moment it is allowed again. */
    setXtExportSwitch(stopped: boolean, reason?: string | null): Observable<XtExportSwitch> {
        return this.http.put<XtExportSwitch>(`${this.base}/central/admin/edges/xt-export`,
            { stopped, reason: reason ?? null });
    }

    // --- The store PC installer published for this environment (merchants:read) ---
    /** What is in this environment's installer bucket, newest first. The middleware reads the bucket the infra
     *  named; the admin never talks to S3 itself. */
    getEdgeInstallerCatalog(): Observable<EdgeInstallerCatalog> {
        return this.http.get<EdgeInstallerCatalog>(`${this.base}/central/admin/edges/installer`);
    }

    /** A link that downloads one package, signed by the middleware and good for 15 minutes. */
    getEdgeInstallerDownload(fileName: string): Observable<EdgeInstallerDownload> {
        return this.http.get<EdgeInstallerDownload>(
            `${this.base}/central/admin/edges/installer/${encodeURIComponent(fileName)}/download`);
    }

    // --- Order hand-off: the method (store Edge or legacy XT POS), its breakers, the failing hand-offs ---

    /** Which system receives online orders, who chose it, each recipient's circuit breaker and the hand-offs that
     *  did not get through in the last day. orders:read. */
    getOrderHandoff(): Observable<OrderHandoff> {
        return this.http.get<OrderHandoff>(`${this.base}/central/admin/order-handoff`);
    }

    /** Switches every location's online orders to the store Edge ('edge') or the legacy XT POS ('xt'). Takes effect
     *  on every instance within seconds; hand-offs already under way are not rewritten. consumers:manage. */
    setOrderHandoff(method: string, reason: string): Observable<OrderHandoff> {
        return this.http.put<OrderHandoff>(`${this.base}/central/admin/order-handoff`, { method, reason });
    }

    /** Orders waiting to be sent with a hand-off method (default: the one in force) from the last `days` days:
     *  a hand-off gave up and nothing is on its way to that method. orders:read. */
    getStalledHandoffs(method?: string, days?: number): Observable<StalledHandoffs> {
        let params = new HttpParams();
        if (method) params = params.set('method', method);
        if (days) params = params.set('days', days);
        return this.http.get<StalledHandoffs>(`${this.base}/central/admin/order-handoff/stalled`, { params });
    }

    /** Sends each order through its per-order lever for the method (Send to store / Send to XT) and answers per
     *  order. At most 200. consumers:manage. */
    sendStalledHandoffs(method: string, orders: { locationId: string; orderId: string; source?: string }[]): Observable<StalledSendResponse> {
        return this.http.post<StalledSendResponse>(`${this.base}/central/admin/order-handoff/send`, { method, orders });
    }

    /** Closes a recipient's breaker by hand and releases the orders it held. consumers:manage. */
    resetHandoffBreaker(recipient: string): Observable<OrderHandoff> {
        return this.http.post<OrderHandoff>(
            `${this.base}/central/admin/order-handoff/breakers/${encodeURIComponent(recipient)}/reset`, {});
    }

    // --- Payments: the merchant behind a location (sandbox switch) ---
    getLocationMerchant(locationId: string): Observable<LocationMerchantInfo> {
        return this.http.get<LocationMerchantInfo>(`${this.base}/payments/admin/locations/${encodeURIComponent(locationId)}/merchant`);
    }

    // MOVE AN ASSIGNMENT TO AN ENVIRONMENT — what replaced the sandbox toggle. Choosing a production
    // environment is how a merchant goes live, and it copies no credential.
    setMethodEnvironment(locationId: string, methodCode: string, environmentId: string): Observable<void> {
        return this.http.put<void>(`${this.base}/payments/admin/locations/${encodeURIComponent(locationId)}/methods/${encodeURIComponent(methodCode)}/environment`, { environmentId });
    }

    // The environments this location may choose for one method (names + kinds; no config values).
    getAssignableEnvironments(locationId: string, methodCode: string): Observable<MethodEnvironmentInfo[]> {
        return this.http.get<MethodEnvironmentInfo[]>(`${this.base}/payments/admin/locations/${encodeURIComponent(locationId)}/methods/${encodeURIComponent(methodCode)}/environments`);
    }

    // The MERCHANT-SPECIFIC half of the method's form, with per-field "is a value stored?" for this
    // assignment. Server-driven, so the merchant editor renders inputs it was never written to know about.
    getMerchantMethodForm(locationId: string, methodCode: string): Observable<MethodConfigField[]> {
        return this.http.get<MethodConfigField[]>(`${this.base}/payments/admin/locations/${encodeURIComponent(locationId)}/methods/${encodeURIComponent(methodCode)}/form`);
    }

    // --- Payments: per-location payment methods (config is write-only) ---
    getPaymentMethods(locationId: string): Observable<PaymentMethodInfo[]> {
        return this.http.get<PaymentMethodInfo[]>(`${this.base}/payments/admin/locations/${encodeURIComponent(locationId)}/methods`);
    }

    getKnownPaymentMethods(locationId: string): Observable<string[]> {
        return this.http.get<string[]>(`${this.base}/payments/admin/locations/${encodeURIComponent(locationId)}/methods/known`);
    }

    // Answers the MATERIALIZED effective configuration the save just produced — no second round trip.
    upsertPaymentMethod(locationId: string, methodCode: string, body: UpsertPaymentMethod): Observable<EffectiveConfigView> {
        return this.http.put<EffectiveConfigView>(`${this.base}/payments/admin/locations/${encodeURIComponent(locationId)}/methods/${encodeURIComponent(methodCode)}`, body);
    }

    // The same readout, for editor open. Reports the bytes a charge is handed, redacted.
    getEffectiveConfig(locationId: string, methodCode: string): Observable<EffectiveConfigView> {
        return this.http.get<EffectiveConfigView>(`${this.base}/payments/admin/locations/${encodeURIComponent(locationId)}/methods/${encodeURIComponent(methodCode)}/effective-config`);
    }

    // --- Payments: a method's FORM and its ENVIRONMENTS (platform-level; payments:manage) ---
    //
    // A method is its logic plus its FORM; an environment is that form filled in and named; a merchant's
    // assignment picks one.

    getMethodForm(code: string): Observable<MethodConfigField[]> {
        return this.http.get<MethodConfigField[]>(`${this.base}/payments/admin/methods/${encodeURIComponent(code)}/form`);
    }

    getMethodEnvironments(code: string): Observable<MethodEnvironmentInfo[]> {
        return this.http.get<MethodEnvironmentInfo[]>(`${this.base}/payments/admin/methods/${encodeURIComponent(code)}/environments`);
    }

    // The form WITH per-field "is a value stored?" for one environment. Presence only — never values.
    getMethodEnvironmentForm(code: string, environmentId: string): Observable<MethodConfigField[]> {
        return this.http.get<MethodConfigField[]>(`${this.base}/payments/admin/methods/${encodeURIComponent(code)}/environments/${encodeURIComponent(environmentId)}/form`);
    }

    createMethodEnvironment(code: string, body: UpsertMethodEnvironment): Observable<{ environmentId: string }> {
        return this.http.post<{ environmentId: string }>(`${this.base}/payments/admin/methods/${encodeURIComponent(code)}/environments`, body);
    }

    updateMethodEnvironment(code: string, environmentId: string, body: UpsertMethodEnvironment): Observable<void> {
        return this.http.put<void>(`${this.base}/payments/admin/methods/${encodeURIComponent(code)}/environments/${encodeURIComponent(environmentId)}`, body);
    }

    deleteMethodEnvironment(code: string, environmentId: string): Observable<void> {
        return this.http.delete<void>(`${this.base}/payments/admin/methods/${encodeURIComponent(code)}/environments/${encodeURIComponent(environmentId)}`);
    }

    // --- Payments: the GLOBAL method catalogue (platform config is write-only) ---
    getGlobalMethods(): Observable<GlobalMethodInfo[]> {
        return this.http.get<GlobalMethodInfo[]>(`${this.base}/payments/admin/methods`);
    }

    getGlobalMethodSchema(code: string): Observable<MethodConfigField[]> {
        return this.http.get<MethodConfigField[]>(`${this.base}/payments/admin/methods/${encodeURIComponent(code)}/schema`);
    }

    upsertGlobalMethod(code: string, body: { displayName: string; enabled: boolean; configJson?: string | null }): Observable<void> {
        return this.http.put<void>(`${this.base}/payments/admin/methods/${encodeURIComponent(code)}`, body);
    }

    // --- Payments view: the event-sourced ledger, projected for the admin (no secrets, no card data) ---
    getPayments(opts: { locationId?: string; status?: string; search?: string; mode?: string; page?: number; pageSize?: number } = {}): Observable<PaymentPage> {
        let params = new HttpParams();
        if (opts.locationId) params = params.set('locationId', opts.locationId);
        if (opts.status) params = params.set('status', opts.status);
        if (opts.search) params = params.set('search', opts.search);
        if (opts.mode) params = params.set('mode', opts.mode);
        params = params.set('page', String(opts.page ?? 1)).set('pageSize', String(opts.pageSize ?? 25));
        return this.http.get<PaymentPage>(`${this.base}/payments/admin/payments`, { params });
    }

    getPaymentDetail(intentId: string): Observable<PaymentDetail> {
        return this.http.get<PaymentDetail>(`${this.base}/payments/admin/payments/${encodeURIComponent(intentId)}`);
    }

    /** The payment's raw event stream — every recorded fact, in order (the audit timeline). */
    getPaymentTimeline(intentId: string): Observable<PaymentTimelineEvent[]> {
        return this.http.get<PaymentTimelineEvent[]>(`${this.base}/payments/admin/payments/${encodeURIComponent(intentId)}/timeline`);
    }

    getPaymentStatuses(): Observable<string[]> {
        return this.http.get<string[]>(`${this.base}/payments/admin/payments/statuses`);
    }

    // --- Admin: payment flows (the payment-to-order saga) ---

    /** Flows that need an operator decision. This list should be empty. */
    getEscalatedPaymentFlows(limit = 100): Observable<PaymentFlow[]> {
        const params = new HttpParams().set('limit', String(limit));
        return this.http.get<PaymentFlow[]>(`${this.base}/payments/admin/flows/escalated`, { params });
    }

    /** What the platform gave back on its own — including what it TRIED to give back and could not. */
    getCompensatedPaymentFlows(limit = 100): Observable<PaymentFlow[]> {
        const params = new HttpParams().set('limit', String(limit));
        return this.http.get<PaymentFlow[]>(`${this.base}/payments/admin/flows/compensations`, { params });
    }

    getPaymentFlow(intentId: string): Observable<PaymentFlow> {
        return this.http.get<PaymentFlow>(`${this.base}/payments/admin/flows/${encodeURIComponent(intentId)}`);
    }

    /** By cart/quote — what a merchant has in hand when a customer calls about a missing order. */
    getPaymentFlowsByReference(locationId: string, clientReference: string): Observable<PaymentFlow[]> {
        const params = new HttpParams().set('locationId', locationId).set('clientReference', clientReference);
        return this.http.get<PaymentFlow[]>(`${this.base}/payments/admin/flows/by-reference`, { params });
    }

    // --- Admin: Cognito users & roles ---
    getGroups(): Observable<CognitoGroup[]> {
        return this.http.get<CognitoGroup[]>(`${this.base}/central/admin/groups`);
    }

    getUsers(): Observable<CognitoUser[]> {
        return this.http.get<CognitoUser[]>(`${this.base}/central/admin/users`);
    }

    addUserToGroup(username: string, group: string): Observable<unknown> {
        return this.http.post(`${this.base}/central/admin/users/${encodeURIComponent(username)}/groups`, { group });
    }

    removeUserFromGroup(username: string, group: string): Observable<unknown> {
        return this.http.delete(`${this.base}/central/admin/users/${encodeURIComponent(username)}/groups/${encodeURIComponent(group)}`);
    }

    getPermissionTree(): Observable<PermissionNode[]> {
        return this.http.get<PermissionNode[]>(`${this.base}/central/admin/permission-tree`);
    }

    // --- Admin: API tokens (issue/rotate/revoke agnostic bearer credentials; perm tokens:manage) ---
    // The secret is returned ONLY by create/rotate (IssuedTokenResponse.token); list never exposes it.
    getApiTokens(): Observable<ApiToken[]> {
        return this.http.get<ApiToken[]>(`${this.base}/central/admin/tokens`);
    }

    createApiToken(body: CreateApiToken): Observable<IssuedTokenResponse> {
        return this.http.post<IssuedTokenResponse>(`${this.base}/central/admin/tokens`, body);
    }

    rotateApiToken(id: string): Observable<IssuedTokenResponse> {
        return this.http.post<IssuedTokenResponse>(`${this.base}/central/admin/tokens/${encodeURIComponent(id)}/rotate`, {});
    }

    setApiTokenEnabled(id: string, enabled: boolean): Observable<ApiToken> {
        return this.http.put<ApiToken>(`${this.base}/central/admin/tokens/${encodeURIComponent(id)}/enabled`, { enabled });
    }

    deleteApiToken(id: string): Observable<void> {
        return this.http.delete<void>(`${this.base}/central/admin/tokens/${encodeURIComponent(id)}`);
    }

    getRolePermissions(): Observable<RolePermissions[]> {
        return this.http.get<RolePermissions[]>(`${this.base}/central/admin/role-permissions`);
    }

    // Bus-outbox backlog: messages the API committed but the delivery service has not yet handed
    // to the broker. The publish-side twin of the per-queue Messages column.
    // Which broker this API instance is on (host, AMQP endpoint, management root). Read once per page load.
    getConsumersBroker(): Observable<BrokerInfo> {
        return this.http.get<BrokerInfo>(`${this.base}/central/admin/consumers/broker`);
    }

    getConsumersOutbox(): Observable<OutboxStatus> {
        return this.http.get<OutboxStatus>(`${this.base}/central/admin/consumers/outbox`);
    }

    // --- Catalog: per-location product catalog (read only; perm merchants:read) ---
    // Event-sourced projection of what the middleware observed from XT. Paged, ordered by SKU.
    getLocationProducts(locationId: string, opts: { page?: number; pageSize?: number; search?: string; familyKey?: string; activeOnly?: boolean; sort?: string; desc?: boolean } = {}): Observable<CatalogPage<CatalogProductListItem>> {
        let params = new HttpParams()
            .set('page', String(opts.page ?? 1))
            .set('pageSize', String(opts.pageSize ?? 50));
        if (opts.search) params = params.set('search', opts.search);
        if (opts.familyKey) params = params.set('familyKey', opts.familyKey);
        if (opts.activeOnly) params = params.set('activeOnly', String(opts.activeOnly));
        // Column sort: sort is a whitelisted key (name/price/changes/lastChanged), desc the direction.
        if (opts.sort) params = params.set('sort', opts.sort);
        if (opts.desc) params = params.set('desc', 'true');
        return this.http.get<CatalogPage<CatalogProductListItem>>(`${this.base}/catalog/locations/${encodeURIComponent(locationId)}/products`, { params });
    }

    // --- Admin IsOnline pins (read: perm products:read, write: perm products:manage) ---
    // NOT part of the catalog projection above: the catalog is what XT said, this is what an operator
    // decided for the same property. Kept on its own /central/products route for that reason.

    // Every product whose IsOnline an operator has pinned at this location, with the pinned value. A SKU
    // absent from the list is not pinned — XT's isOnline decides for it.
    getProductOnlineOverrides(locationId: string): Observable<ProductOnlineOverrideList> {
        return this.http.get<ProductOnlineOverrideList>(
            `${this.base}/central/products/online-override`,
            { params: new HttpParams().set('locationId', locationId) });
    }

    // online=true/false pins IsOnline to that value (idempotent); online=null releases the pin and hands
    // the decision back to XT. Resolves even when Magento could not be pushed — check `pushedToMagento`
    // on the result, because the pin is saved either way.
    setProductOnlineOverride(locationId: string, sku: string, online: boolean | null): Observable<SetProductOnlineOverrideResult> {
        return this.http.put<SetProductOnlineOverrideResult>(
            `${this.base}/central/products/online-override`, { locationId, sku, online });
    }

    // What the middleware last SENT to Magento for one product: the decoded payload of the newest request
    // machine row plus its dispatch state. Its own call rather than part of the catalog detail because the
    // catalog module cannot see ApplicationDbContext — see ProductMagentoPayloadEndpoint for why.
    getProductMagentoPayload(locationId: string, sku: string): Observable<MagentoPayload> {
        return this.http.get<MagentoPayload>(
            `${this.base}/central/products/magento-payload`,
            { params: new HttpParams().set('locationId', locationId).set('sku', sku) });
    }

    // The tax the register charges on one product today, line by line, from the XT replica and the
    // location's resolved tax profile (the product dialog's "Tax at the register" panel).
    getProductTax(locationId: string, sku: string): Observable<ProductTax> {
        return this.http.get<ProductTax>(
            `${this.base}/central/products/tax`,
            { params: new HttpParams().set('locationId', locationId).set('sku', sku) });
    }

    // Current state of one SKU at one location (primary-key load; 404 if never synced here).
    getLocationProduct(locationId: string, sku: string): Observable<CatalogProductDetail> {
        return this.http.get<CatalogProductDetail>(`${this.base}/catalog/locations/${encodeURIComponent(locationId)}/products/${encodeURIComponent(sku)}`);
    }

    // Full change history for one SKU, newest first, paged. Filter by date range and change type.
    getProductHistory(locationId: string, sku: string, opts: { page?: number; pageSize?: number; from?: string; to?: string; changeType?: string } = {}): Observable<CatalogPage<ProductChange>> {
        let params = new HttpParams()
            .set('page', String(opts.page ?? 1))
            .set('pageSize', String(opts.pageSize ?? 50));
        if (opts.from) params = params.set('from', opts.from);
        if (opts.to) params = params.set('to', opts.to);
        if (opts.changeType) params = params.set('changeType', opts.changeType);
        return this.http.get<CatalogPage<ProductChange>>(`${this.base}/catalog/locations/${encodeURIComponent(locationId)}/products/${encodeURIComponent(sku)}/history`, { params });
    }

    // Typed price trail for one SKU, oldest first (unpaged). kind: Regular | OnSale | Cost | Mod.
    getProductPriceHistory(locationId: string, sku: string, opts: { from?: string; to?: string; kind?: string } = {}): Observable<ProductPriceHistory> {
        let params = new HttpParams();
        if (opts.from) params = params.set('from', opts.from);
        if (opts.to) params = params.set('to', opts.to);
        if (opts.kind) params = params.set('kind', opts.kind);
        return this.http.get<ProductPriceHistory>(`${this.base}/catalog/locations/${encodeURIComponent(locationId)}/products/${encodeURIComponent(sku)}/price-history`, { params });
    }

    // A location's product families (size-variant groupings) from the async rollup, paged.
    getLocationFamilies(locationId: string, opts: { page?: number; pageSize?: number; search?: string } = {}): Observable<CatalogPage<FamilyListItem>> {
        let params = new HttpParams()
            .set('page', String(opts.page ?? 1))
            .set('pageSize', String(opts.pageSize ?? 50));
        if (opts.search) params = params.set('search', opts.search);
        return this.http.get<CatalogPage<FamilyListItem>>(`${this.base}/catalog/locations/${encodeURIComponent(locationId)}/families`, { params });
    }

    // One family and all of its size variants.
    getFamilyDetail(locationId: string, familyKey: string): Observable<FamilyDetail> {
        return this.http.get<FamilyDetail>(`${this.base}/catalog/locations/${encodeURIComponent(locationId)}/families/${encodeURIComponent(familyKey)}`);
    }

    // The XT category tree for one location (category -> sub-category). Not paged.
    getLocationCategories(locationId: string): Observable<CatalogCategoryTree> {
        return this.http.get<CatalogCategoryTree>(`${this.base}/catalog/locations/${encodeURIComponent(locationId)}/categories`);
    }

    // Catalog counters for one location (products, families, categories, last change).
    getLocationCatalogStats(locationId: string): Observable<CatalogLocationStats> {
        return this.http.get<CatalogLocationStats>(`${this.base}/catalog/locations/${encodeURIComponent(locationId)}/stats`);
    }

    // --- XT items: one merchant's item master off the XtSyncDb replica (read only; perm merchants:read) ---
    // NOT the catalog above. The catalog is the platform's event-sourced projection of what it OBSERVED
    // from XT; this is the replicated XT table itself, unprojected — which is what makes it the screen to
    // open when the two disagree.
    //
    // The location is in the PATH, not in the query string, and both calls require it: there is no
    // browse-everything mode, so no request can ask for the whole replica. The server refuses a location
    // the platform has not onboarded with a 404 that says so.
    getLocationXtItems(locationId: string, opts: {
        page?: number; pageSize?: number; search?: string; sort?: string; desc?: boolean;
    } = {}): Observable<XtItemPage> {
        let params = new HttpParams()
            .set('page', String(opts.page ?? 1))
            .set('pageSize', String(opts.pageSize ?? 50));
        // Matched case-insensitively against primary_sku OR description, server side. The `total` that
        // comes back reflects it.
        if (opts.search) params = params.set('search', opts.search);
        // Whitelisted column key (sku/price/description); anything else is the item-id order.
        if (opts.sort) params = params.set('sort', opts.sort);
        if (opts.desc) params = params.set('desc', 'true');
        return this.http.get<XtItemPage>(
            `${this.base}/central/admin/locations/${encodeURIComponent(locationId)}/xt-items`, { params });
    }

    // The whole row, raw: every column under its database name, valued as stored. A primary-key load —
    // (itemId, locationId) is the table's real key, so the location in the path is load-bearing: the same
    // item id exists at every location.
    getLocationXtItem(locationId: string, itemId: number): Observable<XtItemRaw> {
        return this.http.get<XtItemRaw>(
            `${this.base}/central/admin/locations/${encodeURIComponent(locationId)}/xt-items/${itemId}`);
    }

    // --- OHLQ: Ohio's statewide catalogue (read-only; the ledger records what the state published) ---
    // Not location-scoped, unlike the product catalog above: OHLQ prices are set by the state, so one
    // item is one stream for the whole estate.
    // The catalogue list: one row per product, sizes included. A product sold in several sizes is the
    // configurable; a one-size product is the simple. Both come back here.
    getOhlqProducts(opts: {
        search?: string; category?: string; visibleOnly?: boolean; multiSizeOnly?: boolean;
        sort?: string; page?: number; pageSize?: number;
    } = {}): Observable<OhlqProductPage> {
        let params = new HttpParams();
        if (opts.search) params = params.set('search', opts.search);
        if (opts.category) params = params.set('category', opts.category);
        if (opts.visibleOnly) params = params.set('visibleOnly', 'true');
        if (opts.multiSizeOnly) params = params.set('multiSizeOnly', 'true');
        if (opts.sort) params = params.set('sort', opts.sort);
        params = params.set('page', String(opts.page ?? 1)).set('pageSize', String(opts.pageSize ?? 50));
        return this.http.get<OhlqProductPage>(`${this.base}/catalog/ohlq/products`, { params });
    }

    getOhlqCatalog(opts: {
        search?: string; category?: string; visibleOnly?: boolean; coverage?: string;
        familyKey?: string; sort?: string; page?: number; pageSize?: number;
    } = {}): Observable<OhlqCatalogPage> {
        let params = new HttpParams();
        if (opts.search) params = params.set('search', opts.search);
        if (opts.category) params = params.set('category', opts.category);
        if (opts.visibleOnly) params = params.set('visibleOnly', 'true');
        if (opts.coverage) params = params.set('coverage', opts.coverage);
        if (opts.familyKey) params = params.set('familyKey', opts.familyKey);
        if (opts.sort) params = params.set('sort', opts.sort);
        params = params.set('page', String(opts.page ?? 1)).set('pageSize', String(opts.pageSize ?? 50));
        return this.http.get<OhlqCatalogPage>(`${this.base}/catalog/ohlq/items`, { params });
    }

    // Current state plus the whole event history for one item — the two halves of the ledger.
    getOhlqItem(itemId: string): Observable<OhlqItemDetail> {
        return this.http.get<OhlqItemDetail>(`${this.base}/catalog/ohlq/items/${encodeURIComponent(itemId)}`);
    }

    getOhlqCategories(): Observable<string[]> {
        return this.http.get<string[]>(`${this.base}/catalog/ohlq/categories`);
    }

    // Trigger a read of both OHLQ feeds. Safe to repeat: an unchanged catalogue appends nothing.
    // asOf is the date the price feed is asked about AND the date "current" is chosen against.
    ingestOhlqCatalog(asOf?: string): Observable<OhlqIngestResult> {
        const params = asOf ? new HttpParams().set('asOf', asOf) : new HttpParams();
        return this.http.post<OhlqIngestResult>(`${this.base}/ohlq/ingest`, {}, { params });
    }

    // Start a tracked background publish of the ledger into Magento's central catalog. Answers
    // immediately with the run id; progress lives on the sync history.
    // Delta by default: only items the ledger says changed since the last successful publish. full=true
    // is the repair lever. Single-flight server-side — a second call answers with the run already going.
    publishOhlqToMagento(opts: { limit?: number; category?: string; full?: boolean } = {}):
        Observable<{ runId: string; alreadyRunning?: boolean }> {
        let params = new HttpParams();
        if (opts.limit) params = params.set('limit', String(opts.limit));
        if (opts.category) params = params.set('category', opts.category);
        if (opts.full) params = params.set('full', 'true');
        return this.http.post<{ runId: string; alreadyRunning?: boolean }>(`${this.base}/ohlq/publish-magento`, {}, { params });
    }

    // --- Faux merchants: synthetic ohio liquor stores living in the middleware only.
    //     Types live below the class (FauxMerchant, FauxDayHours). ---
    getFauxMerchants(): Observable<FauxMerchant[]> {
        return this.http.get<FauxMerchant[]>(`${this.base}/central/admin/faux-merchants/`);
    }

    generateFauxMerchants(): Observable<{ total: number; created: number }> {
        return this.http.post<{ total: number; created: number }>(`${this.base}/central/admin/faux-merchants/generate`, {});
    }

    // Materialise one in Magento: real provisioning, location registration, hours push. An UPSERT —
    // an existing merchant is updated and its provisioning re-walked, so pressing again always heals.
    createFauxMerchantInMagento(id: string):
        Observable<{ merchant: FauxMerchant; createdNow: boolean; registeredLocation: boolean; hoursPushed: boolean }> {
        return this.http.post<{ merchant: FauxMerchant; createdNow: boolean; registeredLocation: boolean; hoursPushed: boolean }>(
            `${this.base}/central/admin/faux-merchants/${id}/create-magento`, {});
    }

    // The fast fill: bulk-copy the sellable central catalog into the store as its own products.
    copyFauxMerchantCatalog(id: string): Observable<{ merchant: FauxMerchant; copy: unknown }> {
        return this.http.post<{ merchant: FauxMerchant; copy: unknown }>(
            `${this.base}/central/admin/faux-merchants/${id}/copy-catalog`, {});
    }

    updateFauxMerchantHours(id: string, body: { deliveryHours: Record<string, FauxDayHours>; pickupHours: Record<string, FauxDayHours> }):
        Observable<{ merchant: FauxMerchant; hoursPushed: boolean }> {
        return this.http.put<{ merchant: FauxMerchant; hoursPushed: boolean }>(
            `${this.base}/central/admin/faux-merchants/${id}/hours`, body);
    }

    // The gallery-visibility heal: one visible image per ohio item. Idempotent — zeros when healed.
    healOhlqGallery(): Observable<{ shown: number; hidden: number }> {
        return this.http.post<{ shown: number; hidden: number }>(`${this.base}/ohlq/heal-gallery`, {});
    }

    // The onboarded merchant locations a synthetic assortment can target — the send panel's picker.
    getOhlqStores(): Observable<{ stores: { locationId: string; code: string; storeViewId: number }[] }> {
        return this.http.get<{ stores: { locationId: string; code: string; storeViewId: number }[] }>(`${this.base}/ohlq/stores`);
    }

    // Send a synthetic OHLQ assortment to ONE merchant location — the mimic lever. Products travel
    // the real pipeline (one UpsertMagentoProductEvent per item), then the run stocks them. skus=
    // sends exactly those items, count= a stable sample, neither refreshes the store's existing
    // assortment (delta'd unless full). Single-flight per location; runs land in the sync history.
    publishOhlqToStore(opts: { location: string; skus?: string; all?: boolean; count?: number; full?: boolean }):
        Observable<{ runId: string; alreadyRunning?: boolean }> {
        let params = new HttpParams().set('location', opts.location);
        if (opts.skus) params = params.set('skus', opts.skus);
        if (opts.all) params = params.set('all', 'true');
        if (opts.count) params = params.set('count', String(opts.count));
        if (opts.full) params = params.set('full', 'true');
        return this.http.post<{ runId: string; alreadyRunning?: boolean }>(`${this.base}/ohlq/publish-store`, {}, { params });
    }

    // Every sync run — ingests and publishes — newest first, counters live while a run works.
    getOhlqSyncs(): Observable<OhlqSyncRun[]> {
        return this.http.get<OhlqSyncRun[]>(`${this.base}/ohlq/syncs`);
    }

    // --- Family assembly (magento-agent; reads perm products:read, heal perm products:manage) ---
    // Publisher-side family estate: the Magento assembly truth (FAM- parents, links, per-member
    // exclusion reasons) that the read-only catalog rollup deliberately omits.
    getProductFamilies(opts: { locationId?: string; search?: string; page?: number; pageSize?: number } = {}): Observable<ProductFamilyPage> {
        let params = new HttpParams()
            .set('page', String(opts.page ?? 1))
            .set('pageSize', String(opts.pageSize ?? 25));
        if (opts.locationId) params = params.set('locationId', opts.locationId);
        if (opts.search) params = params.set('search', opts.search);
        return this.http.get<ProductFamilyPage>(`${this.base}/magento-agent/product-families`, { params });
    }

    // One family's members with per-member assembly status (Linked / NoSize / DuplicateSize / …).
    getProductFamilyAssembly(familyKey: string): Observable<ProductFamilyAssemblyDetail> {
        return this.http.get<ProductFamilyAssemblyDetail>(`${this.base}/magento-agent/product-families/${encodeURIComponent(familyKey)}`);
    }

    // Heal site families: QUEUES a run (202) and returns it immediately — the pass executes on the
    // bus, immune to timeouts, resumable via redelivery. Poll getHealRun for progress + report.
    // Re-posting while a run is alive returns that same run.
    // recalculateFirst: unlink the store's members so sizes re-derive (volume_metric first) and
    // every family re-runs the full assembly instead of fast-pathing past existing stamps.
    healSiteFamilies(locationId: string, recalculateFirst = false): Observable<HealRun> {
        return this.http.post<HealRun>(`${this.base}/magento-agent/product-families/heal`, { locationId, recalculateFirst });
    }

    // One heal run's progress; carries the final report once status is Completed.
    getHealRun(runId: string): Observable<HealRun> {
        return this.http.get<HealRun>(`${this.base}/magento-agent/product-families/heal-runs/${encodeURIComponent(runId)}`);
    }

    // Newest run for a location (404 when none) — lets the tab resume polling after a refresh.
    getLatestHealRun(locationId: string): Observable<HealRun> {
        const params = new HttpParams().set('locationId', locationId);
        return this.http.get<HealRun>(`${this.base}/magento-agent/product-families/heal-runs/latest`, { params });
    }

    // --- Orders: per-location order book (read only; perm orders:read) ---
    // Event-sourced projection of observed order facts. Newest first, paged.
    getLocationOrders(locationId: string, opts: { page?: number; pageSize?: number; status?: string; source?: string; search?: string; deliveryStatus?: string; from?: string; to?: string } = {}): Observable<LocationOrderPage> {
        let params = new HttpParams()
            .set('page', String(opts.page ?? 1))
            .set('pageSize', String(opts.pageSize ?? 50));
        if (opts.status) params = params.set('status', opts.status);
        if (opts.source) params = params.set('source', opts.source);
        if (opts.search) params = params.set('search', opts.search);
        if (opts.deliveryStatus) params = params.set('deliveryStatus', opts.deliveryStatus);
        if (opts.from) params = params.set('from', opts.from);
        if (opts.to) params = params.set('to', opts.to);
        return this.http.get<LocationOrderPage>(`${this.base}/orders/locations/${encodeURIComponent(locationId)}/orders`, { params });
    }

    // Current state of one order (canonical state + lifecycle block). Pass source when the id is
    // not unique at the location (otherwise a 400 naming the candidate sources).
    getLocationOrder(locationId: string, externalOrderId: string, source?: string): Observable<LocationOrderDetail> {
        let params = new HttpParams();
        if (source) params = params.set('source', source);
        return this.http.get<LocationOrderDetail>(`${this.base}/orders/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}`, { params });
    }

    // Order counters for one location (orders, dispatch backlog, dispatch failures, last activity).
    getLocationOrderStats(locationId: string): Observable<OrderLocationStats> {
        return this.http.get<OrderLocationStats>(`${this.base}/orders/locations/${encodeURIComponent(locationId)}/stats`);
    }

    // Full change timeline for one order (snapshot diffs + lifecycle facts), newest first, paged.
    // Pass source when the order id is not unique at the location. category: Snapshot | Lifecycle.
    getLocationOrderTimeline(locationId: string, externalOrderId: string, opts: { source?: string; page?: number; pageSize?: number; from?: string; to?: string; category?: string; changeType?: string } = {}): Observable<OrderTimelinePage> {
        let params = new HttpParams()
            .set('page', String(opts.page ?? 1))
            .set('pageSize', String(opts.pageSize ?? 50));
        if (opts.source) params = params.set('source', opts.source);
        if (opts.from) params = params.set('from', opts.from);
        if (opts.to) params = params.set('to', opts.to);
        if (opts.category) params = params.set('category', opts.category);
        if (opts.changeType) params = params.set('changeType', opts.changeType);
        return this.http.get<OrderTimelinePage>(`${this.base}/orders/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/timeline`, { params });
    }

    /** The order's raw event stream — every recorded fact with its full payload. Source is required
     *  (it is part of the stream's identity: location|source|orderId). */
    getLocationOrderEvents(locationId: string, externalOrderId: string, source: string): Observable<OrderStreamEvent[]> {
        const params = new HttpParams().set('source', source);
        return this.http.get<OrderStreamEvent[]>(`${this.base}/orders/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/events`, { params });
    }

    // Typed status trail for one order — three parallel tracks, oldest first, unpaged.
    // track narrows to Order | Delivery | Dispatch; source disambiguates a non-unique id.
    getLocationOrderStatusHistory(locationId: string, externalOrderId: string, opts: { source?: string; track?: string } = {}): Observable<OrderStatusHistory> {
        let params = new HttpParams();
        if (opts.source) params = params.set('source', opts.source);
        if (opts.track) params = params.set('track', opts.track);
        return this.http.get<OrderStatusHistory>(`${this.base}/orders/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/status-history`, { params });
    }

    // Timeline filter vocabulary (categories + change types), so the UI's filters are not hardcoded.
    getOrderChangeTypes(): Observable<OrderChangeVocabulary> {
        return this.http.get<OrderChangeVocabulary>(`${this.base}/orders/change-types`);
    }

    // --- XT integration: platform connection (instances:manage) + per-order dispatch records ---
    getXtConnection(): Observable<XtConnection> {
        return this.http.get<XtConnection>(`${this.base}/central/integrations/xt/`);
    }

    // Write-only auth key: blank/omitted keeps the stored one.
    upsertXtConnection(body: UpsertXtConnection): Observable<void> {
        return this.http.put<void>(`${this.base}/central/integrations/xt/`, body);
    }

    // Validates the STORED connection (save first, then ping).
    pingXtConnection(): Observable<XtPingResult> {
        return this.http.post<XtPingResult>(`${this.base}/central/integrations/xt/ping`, {});
    }

    // Dispatch records for one order (perm orders:read) — which downstream systems have been NOTIFIED
    // about it. source disambiguates cross-channel id clashes.
    getOrderDispatches(locationId: string, externalOrderId: string, source?: string): Observable<OrderDispatches> {
        let params = new HttpParams();
        if (source) params = params.set('source', source);
        return this.http.get<OrderDispatches>(
            `${this.base}/central/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/dispatches`, { params });
    }

    // Manual send to XT (perm recipients:manage). Idempotent: once the order carries an XT order id the
    // server answers success with that id and queues NOTHING (alreadyRegistered) — a duplicate POS order
    // needs the API-only ?force=true, which this client deliberately never sends. A send that actually
    // queues creates a NEW dispatch record, never a rewrite of an old one.
    sendOrderToXt(locationId: string, externalOrderId: string, source?: string): Observable<SendToXtResult> {
        let params = new HttpParams();
        if (source) params = params.set('source', source);
        return this.http.post<SendToXtResult>(
            `${this.base}/central/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/send-to-xt`, {}, { params });
    }

    // Manual send to the location's store Edge (perm recipients:manage). Always a deliberate re-send: a store that has
    // the order answers with its current status, and one that refused it offers it to its operators again. Refused
    // (409) for a store's own sale and for a location with no Edge.
    sendOrderToEdge(locationId: string, externalOrderId: string, source?: string): Observable<SendToEdgeResult> {
        let params = new HttpParams();
        if (source) params = params.set('source', source);
        return this.http.post<SendToEdgeResult>(
            `${this.base}/central/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/send-to-edge`, {}, { params });
    }

    // --- DoorDash Drive: the connection, the per-location store ids, and what it costs ---
    getDoorDashConnection(): Observable<DoorDashConnection> {
        return this.http.get<DoorDashConnection>(`${this.base}/central/integrations/doordash/`);
    }

    upsertDoorDashConnection(body: UpsertDoorDashConnection): Observable<void> {
        return this.http.put<void>(`${this.base}/central/integrations/doordash/`, body);
    }

    // Which store DoorDash knows a location as. Set on its own so a Magento location upsert cannot
    // overwrite it.

    // Onboarded stores + their DoorDash state. verify=true costs one developer-API call per merchant, so it
    // is asked for explicitly rather than on every page load.
    getDeliveryStores(verify = false): Observable<DeliveryStoreRow[]> {
        const params = new HttpParams().set('verify', String(verify));
        return this.http.get<DeliveryStoreRow[]>(`${this.base}/central/delivery/stores/`, { params });
    }

    // Registers the store AT DoorDash; the middleware keeps the id only once DoorDash accepted it.
    registerDeliveryStore(locationId: string, body: RegisterDeliveryStore): Observable<DeliveryStoreRow> {
        return this.http.post<DeliveryStoreRow>(
            `${this.base}/central/delivery/stores/${encodeURIComponent(locationId)}/register`, body);
    }

    // Rewrites DoorDash's copy of an already-registered store from the merchant record. One direction only.
    // Answers 409 with a "press Register" message when DoorDash has no such store, so it is safe to press.
    updateDeliveryStoreDetails(locationId: string, body: UpdateDeliveryStoreDetails): Observable<DeliveryStoreRow> {
        return this.http.put<DeliveryStoreRow>(
            `${this.base}/central/delivery/stores/${encodeURIComponent(locationId)}/details`, body);
    }

    getDeliveryCosts(opts: { from?: string; to?: string; locationId?: string } = {}): Observable<DeliveryCostReport> {
        let params = new HttpParams();
        if (opts.from) params = params.set('from', opts.from);
        if (opts.to) params = params.set('to', opts.to);
        if (opts.locationId) params = params.set('locationId', opts.locationId);
        return this.http.get<DeliveryCostReport>(`${this.base}/central/delivery/costs`, { params });
    }

    /** The individual deliveries behind the spend, each cost separated. Same window as getDeliveryCosts. */
    getDeliveryCostDetails(opts: { from?: string; to?: string; locationId?: string; limit?: number } = {}): Observable<DeliveryCostDetail[]> {
        let params = new HttpParams();
        if (opts.from) params = params.set('from', opts.from);
        if (opts.to) params = params.set('to', opts.to);
        if (opts.locationId) params = params.set('locationId', opts.locationId);
        if (opts.limit) params = params.set('limit', String(opts.limit));
        return this.http.get<DeliveryCostDetail[]>(`${this.base}/central/delivery/costs/deliveries`, { params });
    }

    // --- DoorDash quote diagnostics: the switch, the log, and the bin -------------------------------
    // Same permission as the connection itself (instances:manage), not a reporting one: these rows carry
    // customers' dropoff addresses and have to be gated as strictly as the credentials that produced them.

    /** Whether tracing is on, how many traces are held, and whether there is a connection to hang the
     *  flag off at all (`configured` false = the PUT will refuse with 422). */
    getDeliveryTraceSettings(): Observable<DeliveryTraceSettings> {
        return this.http.get<DeliveryTraceSettings>(`${this.base}/central/delivery/diagnostics/settings`);
    }

    /** Flips tracing. Its OWN route, deliberately — turning a debug switch on must never mean re-posting
     *  the signing secret through the credential form. 422 when no connection is saved. */
    setDeliveryTracing(enabled: boolean): Observable<void> {
        return this.http.put<void>(`${this.base}/central/delivery/diagnostics/settings`, { enabled });
    }

    /** Traced attempts, newest first. `outcome` is quoted|refused|error; an unrecognised value filters
     *  nothing rather than everything. */
    getDeliveryTraces(opts: { locationId?: string; outcome?: string; page?: number; pageSize?: number } = {}): Observable<DeliveryTracePage> {
        let params = new HttpParams();
        if (opts.locationId) params = params.set('locationId', opts.locationId);
        if (opts.outcome) params = params.set('outcome', opts.outcome);
        if (opts.page) params = params.set('page', String(opts.page));
        if (opts.pageSize) params = params.set('pageSize', String(opts.pageSize));
        return this.http.get<DeliveryTracePage>(`${this.base}/central/delivery/diagnostics/traces`, { params });
    }

    /** Deletes EVERY stored trace — the addresses in them included. The button for when the
     *  reproduction is over and there is no reason to keep a list of customers' homes on the platform. */
    clearDeliveryTraces(): Observable<{ deleted: number }> {
        return this.http.delete<{ deleted: number }>(`${this.base}/central/delivery/diagnostics/traces`);
    }

    // The order's delivery, provider-agnostic (perm orders:read): where it is, who is carrying it, the
    // tracking link, and every courier update with its raw body.
    getOrderDelivery(locationId: string, externalOrderId: string, source?: string): Observable<OrderDeliveryDetail> {
        let params = new HttpParams();
        if (source) params = params.set('source', source);
        return this.http.get<OrderDeliveryDetail>(
            `${this.base}/central/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/delivery`, { params });
    }

    // Re-runs this order's delivery webhooks. Idempotent — an already-applied event is a no-op — so this
    // is the drain for webhooks that arrived before the order existed.
    replayOrderDelivery(locationId: string, externalOrderId: string, source?: string): Observable<ReplayWebhooksResult> {
        let params = new HttpParams();
        if (source) params = params.set('source', source);
        return this.http.post<ReplayWebhooksResult>(
            `${this.base}/central/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/delivery/replay`, {}, { params });
    }

    // Sets the order's fulfilment status in XT (perm recipients:manage). The lever for what the DoorDash
    // webhooks can't cover — pickup orders, and completions XT refused while it was unreachable. A
    // refusal comes back as a 422 carrying XT's own reason, so the caller can show it verbatim.
    // 'complete' is idempotent server-side: a success already on record answers alreadyApplied=true
    // without calling XT again. 'ready'/'out' are correction pushes and always call.
    setOrderXtStatus(
        locationId: string, externalOrderId: string,
        status: XtFulfilmentStatus = 'complete', source?: string
    ): Observable<SetXtStatusResult> {
        let params = new HttpParams().set('status', status);
        if (source) params = params.set('source', source);
        return this.http.post<SetXtStatusResult>(
            `${this.base}/central/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/xt-status`, {}, { params });
    }

    // Tells the store Edge the order's fulfilment status (perm recipients:manage) — the store-side twin of
    // setOrderXtStatus. The courier webhooks push out-for-delivery and complete on their own; this is the lever
    // for a carrier shipment with a tracking number, a pickup collected at the counter, a phone order finished.
    // The answer is "the store's queue has it": the store applies it when it reads its queue and reports back.
    // 409 = the order was never sent to a store Edge; 422 = the broker refused the send (recorded on the order).
    // 'complete' converges server-side (alreadyApplied); the other three always send.
    setOrderEdgeStatus(
        locationId: string, externalOrderId: string, body: SetEdgeStatusRequest, source?: string
    ): Observable<SetEdgeStatusResult> {
        let params = new HttpParams();
        if (source) params = params.set('source', source);
        return this.http.post<SetEdgeStatusResult>(
            `${this.base}/central/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/edge-status`, body, { params });
    }

    // Tells the store Edge to stop working on the order (perm recipients:manage) — the STORE ONLY. It does not
    // reverse the order in Magento and does not refund the card; the host closure does both and tells the store on
    // its own. 409 = the order was never sent to a store Edge.
    cancelOrderAtEdge(
        locationId: string, externalOrderId: string, reason: string | null, source?: string
    ): Observable<CancelOrderAtEdgeResult> {
        let params = new HttpParams();
        if (source) params = params.set('source', source);
        return this.http.post<CancelOrderAtEdgeResult>(
            `${this.base}/central/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/edge-cancel`, { reason }, { params });
    }

    // The order's instant-print record (perm orders:read): whether a ticket exists, whether the store's
    // print station has fetched it, and whether the location's instant-print gate is even open.
    getOrderPrintState(locationId: string, externalOrderId: string, source?: string): Observable<OrderPrintState> {
        let params = new HttpParams();
        if (source) params = params.set('source', source);
        return this.http.get<OrderPrintState>(
            `${this.base}/central/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/print`, { params });
    }

    // Force re-print (perm recipients:manage): resets the existing print ticket so the printer's next
    // poll picks the order up again, or composes one when the order never had a ticket. A refusal (e.g.
    // instant printing disabled for the location) comes back as a 422 carrying the reason verbatim.
    reprintOrder(locationId: string, externalOrderId: string, source?: string): Observable<ReprintOrderResult> {
        let params = new HttpParams();
        if (source) params = params.set('source', source);
        return this.http.post<ReprintOrderResult>(
            `${this.base}/central/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/reprint`, {}, { params });
    }

    // The receipt content (perm orders:read): the order's print ticket — stored one verbatim, or
    // composed on the fly through the same factory the printer path uses — plus header identity.
    // Read-through; nothing is persisted by viewing it.
    getOrderReceipt(locationId: string, externalOrderId: string, source?: string): Observable<OrderReceipt> {
        let params = new HttpParams();
        if (source) params = params.set('source', source);
        return this.http.get<OrderReceipt>(
            `${this.base}/central/locations/${encodeURIComponent(locationId)}/orders/${encodeURIComponent(externalOrderId)}/receipt`, { params });
    }
}

// --- Faux merchants (synthetic ohio liquor stores; see FauxMerchantsEndpoint) ---

export interface FauxDayHours {
    enabled: boolean;
    open: string;   // "09:00"
    close: string;  // "21:00"
}

export interface FauxMerchant {
    id: string;
    name: string;
    merchantCode: string;
    locationId: string;
    email: string;
    phone: string;
    street: string;
    city: string;
    county: string;
    state: string;
    postcode: string;
    latitude: number;
    longitude: number;
    deliveryHours: Record<string, FauxDayHours> | null;
    pickupHours: Record<string, FauxDayHours> | null;
    createdInMagento: boolean;
    createdInMagentoAt: string | null;
    catalogCopiedAt: string | null;
    magentoMerchantId: number | null;
    magentoWebsiteId: number | null;
    magentoStoreViewId: number | null;
    magentoResources: unknown;
}
